import { useState, useMemo } from 'react'
import {
  useEventChecklist,
  useAddChecklistItem,
  usePatchChecklistItem,
  useDeleteChecklistItem
} from '../hooks/useChecklists'
import { parseIsoDate } from '../lib/dates'
import { useArchived } from '../contexts/ArchiveContext'
import { ChecklistTemplatePickerModal } from './ChecklistTemplatePickerModal'
import { ChecklistItemEditor } from './ChecklistItemEditor'
import { Icon } from './Icon'
import type { EventChecklistItem } from '../types'

// ChecklistsTab - per-event production task list. Items grouped by section
// (Pre-event / Day of / Post-event by default; sections are free-text so
// admins can add their own). Each row is a checkbox + title + meta column
// (owner avatar, due-day pill, notes hint). Optimistic tick toggles via
// patch mutation; section grouping is derived in a useMemo.
//
// Empty state shows two CTAs: Apply a template (opens the picker modal)
// and Add an item ad-hoc.

interface Props {
  eventId: number
  eventStartDate: string
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function ChecklistsTab({ eventId, eventStartDate }: Props) {
  const archived = useArchived()
  const { data, isLoading, error } = useEventChecklist(eventId)
  const addMut = useAddChecklistItem(eventId)
  const patchMut = usePatchChecklistItem(eventId)
  const deleteMut = useDeleteChecklistItem(eventId)

  const [showPicker, setShowPicker] = useState(false)
  const [editing, setEditing] = useState<EventChecklistItem | null>(null)
  const [creatingInSection, setCreatingInSection] = useState<string | null>(null)

  const grouped = useMemo(() => {
    const map = new Map<string, EventChecklistItem[]>()
    for (const it of data?.items ?? []) {
      const arr = map.get(it.section) ?? []
      arr.push(it)
      map.set(it.section, arr)
    }
    return Array.from(map.entries()).map(([section, items]) => ({ section, items }))
  }, [data])

  const totalDone = (data?.items ?? []).filter((i) => i.isDone).length
  const totalAll = (data?.items ?? []).length

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading checklist…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load checklist</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  function toggleDone(item: EventChecklistItem) {
    patchMut.mutate({ itemId: item.id, body: { isDone: !item.isDone } })
  }

  function handleAdd(body: Parameters<typeof addMut.mutate>[0]) {
    addMut.mutate(body, {
      onSuccess: () => setCreatingInSection(null)
    })
  }

  function handleEditSave(body: Parameters<typeof addMut.mutate>[0]) {
    if (!editing) return
    patchMut.mutate(
      { itemId: editing.id, body },
      { onSuccess: () => setEditing(null) }
    )
  }

  function handleDelete() {
    if (!editing) return
    const ok = window.confirm(`Delete "${editing.title}"? This can't be undone.`)
    if (!ok) return
    deleteMut.mutate(editing.id, { onSuccess: () => setEditing(null) })
  }

  const checklistExists = data?.checklist != null

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Checklists</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            Production tasks across pre-event prep, day-of, and post-event teardown.
            {totalAll > 0 && (
              <span className="ml-2 font-mono">
                {totalDone} / {totalAll} complete
              </span>
            )}
          </p>
        </div>
        {!archived && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="px-3 py-2 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
            >
              + Apply template
            </button>
            <button
              type="button"
              onClick={() => setCreatingInSection(grouped[0]?.section ?? 'Pre-event')}
              className="px-3 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
            >
              + Add item
            </button>
          </div>
        )}
      </div>

      {!checklistExists && totalAll === 0 && (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f5edd5', color: '#9b7f2a' }}
          >
            <Icon name="check-circle" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No checklist yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Apply a starter template to populate the standard tasks for this
            type of event, or add items one at a time. Items inherit due
            dates computed from the event's start date.
          </p>
          {!archived && (
            <div className="flex gap-2 justify-center">
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
              >
                Apply a template
              </button>
              <button
                type="button"
                onClick={() => setCreatingInSection('Pre-event')}
                className="px-4 py-2 rounded text-sm font-semibold border border-line text-ink-soft hover:text-green hover:border-green transition-colors"
              >
                Add an item
              </button>
            </div>
          )}
        </div>
      )}

      {grouped.length > 0 && (
        <div className="space-y-4">
          {grouped.map(({ section, items }) => (
            <div key={section} className="bg-white border border-line rounded">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-line">
                <h3 className="text-sm font-semibold flex-1">{section}</h3>
                <span className="text-xs text-ink-soft font-mono">
                  {items.filter((i) => i.isDone).length} / {items.length}
                </span>
                {!archived && (
                  <button
                    type="button"
                    onClick={() => setCreatingInSection(section)}
                    className="text-xs font-semibold text-green hover:underline"
                  >
                    + Add
                  </button>
                )}
              </div>
              <ul className="divide-y divide-line">
                {items.map((item) => (
                  <ChecklistRow
                    key={item.id}
                    item={item}
                    eventStartDate={eventStartDate}
                    archived={archived}
                    pending={patchMut.isPending && patchMut.variables?.itemId === item.id}
                    onToggle={() => toggleDone(item)}
                    onOpen={() => setEditing(item)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {!archived && showPicker && (
        <ChecklistTemplatePickerModal eventId={eventId} onClose={() => setShowPicker(false)} />
      )}

      {!archived && creatingInSection && (
        <ChecklistItemEditor
          item={null}
          defaultSection={creatingInSection}
          saving={addMut.isPending}
          serverError={addMut.error as Error | null}
          onClose={() => setCreatingInSection(null)}
          onSave={handleAdd}
        />
      )}

      {!archived && editing && (
        <ChecklistItemEditor
          item={editing}
          saving={patchMut.isPending}
          deleting={deleteMut.isPending}
          serverError={(patchMut.error ?? deleteMut.error) as Error | null}
          onClose={() => setEditing(null)}
          onSave={handleEditSave}
          onDelete={handleDelete}
        />
      )}
    </div>
  )
}

// One row in a section's item list. Checkbox + title + meta column.
function ChecklistRow({
  item, eventStartDate, archived, pending, onToggle, onOpen
}: {
  item: EventChecklistItem
  eventStartDate: string
  archived: boolean
  pending: boolean
  onToggle: () => void
  onOpen: () => void
}) {
  const overdue = !item.isDone && item.dueDate && item.dueDate < todayIso()
  return (
    <li className="px-4 py-3 flex items-start gap-3 hover:bg-parchment transition-colors">
      <button
        type="button"
        onClick={onToggle}
        aria-label={item.isDone ? 'Mark as not done' : 'Mark as done'}
        disabled={pending || archived}
        className={
          'mt-0.5 w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ' +
          (item.isDone
            ? 'bg-green border-green text-parchment'
            : 'bg-white border-line hover:border-green') +
          (archived ? ' cursor-default' : '')
        }
      >
        {item.isDone ? '✓' : ''}
      </button>
      <button
        type="button"
        onClick={onOpen}
        disabled={archived}
        className={'flex-1 min-w-0 text-left' + (archived ? ' cursor-default' : '')}
      >
        <div
          className={
            'text-sm leading-tight ' +
            (item.isDone ? 'line-through text-ink-soft' : 'font-semibold')
          }
        >
          {item.title}
        </div>
        {item.description && (
          <p
            className="text-xs text-ink-soft mt-0.5"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {item.description}
          </p>
        )}
      </button>
      <div className="flex items-center gap-3 flex-shrink-0 pt-0.5">
        {item.owner ? (
          <div
            className="w-6 h-6 rounded-full bg-green text-parchment text-[10px] font-semibold flex items-center justify-center"
            title={item.owner.name ?? 'Owner'}
          >
            {item.owner.avatarInitials ?? '?'}
          </div>
        ) : (
          <span className="text-xs italic text-ink-soft" title="No owner assigned">—</span>
        )}
        {item.dueDate && (
          <span
            className={
              'text-[11px] font-mono px-1.5 py-0.5 rounded ' +
              (overdue
                ? 'bg-oxblood-soft text-oxblood'
                : item.isDone
                ? 'bg-green-soft text-green'
                : 'bg-parchment text-ink-soft')
            }
            title={item.dueDate}
          >
            {dueLabel(item.dueDate, eventStartDate)}
          </span>
        )}
      </div>
    </li>
  )
}

function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function dueLabel(dueIso: string, _eventStartIso: string): string {
  const d = parseIsoDate(dueIso)
  return `${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]}`
}
